import BandCard from "../../components/BandCard";
import Button from "../../components/Button";
import ProgressBar from "../../components/ProgressBar";
import { useEffect, useState } from "react";
import { useHeaderContext } from "../../context/headerContext";
import { useLocation } from "react-router-dom";
import DropDown from "../../components/Dropdown";
import CheckBox from "../../components/CheckBox";
import { Link } from "react-router-dom";
import VoiceNote from "../../components/VoiceNote";
import EditPencilIcon from "/public/images/icons/editPencil.svg?react";
import AddIcon from "/public/images/icons/add.svg?react";
import { useUserContext } from "../../context/userContext";
import { useNavigate } from "react-router-dom";
import { useDrawerContext } from "../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import Toggle from "../../components/Toggle";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function AddBandComplete() {
  const navigate = useNavigate();
  const { setTitle, setBackButton } = useHeaderContext();
  const { state } = useLocation();
  const { band, profile } = state;
  const { user, logout, updateTokens } = useUserContext();
  const [activatingBand, setActivatingBand] = useState(false);
  const {setOpen, setDrawerContent} = useDrawerContext();
  const [error, setError] = useState("");

  useEffect(() => {
    setTitle("Review Details");
    setBackButton(true);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // const base64ToFile = (base64Data, fileName, contentType) => {
  //   const byteCharacters = atob(base64Data.split(',')[1]); // Extract the Base64 part after the comma
  //   const byteArrays = [];
  
  //   for (let i = 0; i < byteCharacters.length; i++) {
  //     byteArrays.push(byteCharacters.charCodeAt(i));
  //   }
  
  //   const byteArray = new Uint8Array(byteArrays);
  //   const blob = new Blob([byteArray], { type: contentType });
  
  //   // Create a File object from the Blob, with the original file name
  //   return new File([blob], fileName, { type: contentType });
  // };

  const step2Answers = JSON.parse(localStorage.getItem("activeBandStep2"));
  const step2AnswersDelete = JSON.parse(localStorage.getItem("activeBandStep2Delete"));
  const step3Answers = JSON.parse(localStorage.getItem("activeBandStep3"));
  const step3AnswersDelete = JSON.parse(localStorage.getItem("activeBandStep3Delete"));
  const step4Answers = JSON.parse(localStorage.getItem("activeBandStep4"));
  const step4AnswersDelete = JSON.parse(localStorage.getItem("activeBandStep4Delete"));
  const step5Answers = JSON.parse(localStorage.getItem("activeBandStep5"));
  const step5AnswersDelete = JSON.parse(localStorage.getItem("activeBandStep5Delete"));

  const handleBandActivation = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    const formData = new FormData(e.target);
    const isConfirmed = formData.get('confirmation') === 'on';

    if (!isConfirmed) {
      setError("Please confirm all the information you have provided is correct. You can update your information anytime from your profile page.");
      LogErrors(user, logout, updateTokens, "Please confirm all the information you have provided is correct. You can update your information anytime from your profile page.");
      return;
    }

    setError(""); // Clear any previous error
    setActivatingBand(true);
    let allSuccessful = true; // Flag to track success of all API calls
    
    const startBandActivation = async () => {
      await Sentry.startSpan(
        { name: "Start Band Activation", op: "http.request" },
        async (span) => {
          try {
            const response = await fetchWithRefresh(import.meta.env.VITE_API_URL + `Bands/BeginBandActivation/?participantId=${band.ParticipantId}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); });
    
            span.setAttribute("http.status_code", response.status);
            if (!response.ok) span.setStatus("error");
          } catch (error) {
            span.setStatus("error");
            Sentry.captureException(error);
            LogErrors(user, logout, updateTokens, "Failed to start activate band process: " + error);
          } finally {
            span.end();
          }
        }
      );
    };    

    // Save User Profile
    const saveUserProfile = async () => {
      await Sentry.startSpan(
        { name: "Save User Profile", op: "http.request" },
        async (span) => {
          try {
            const response = await fetchWithRefresh(import.meta.env.VITE_API_URL + "Profile/SaveUserProfile", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: band.ParticipantId,
                participantName: step2Answers.participantName,
                participantDateOfBirth: step2Answers.participantDOB,
                primaryICEName: step2Answers.bandICEName,
                primaryICENumber: step2Answers.bandICEPhoneNumber,
                primaryICERelationship: step2Answers.bandICERelation,
                PrimaryMedicalCondition: step2Answers.PrimaryMedicalCondition,
                EmergencySMSPrimaryContact: step2Answers.EmergencySMSPrimaryContact === "1" || step2Answers.EmergencySMSPrimaryContact === "on" ? 1 : 0,
                notes: step2Answers.participantNotes,
                audioURL: step2Answers?.uploadedVoiceNote || "",
                profilePicture: step2Answers.profilePicture
              }),
            }, logout, updateTokens, (message) => { toast.info(message); });
    
            span.setAttribute("http.status_code", response.status);
            if (!response.ok) {
              span.setStatus("error");
              allSuccessful = false;
            }
          } catch (error) {
            span.setStatus("error");
            Sentry.captureException(error);
            LogErrors(user, logout, updateTokens, "Error saving user's profile: " + error);
            allSuccessful = false;
          } finally {
            span.end();
          }
        }
      );
    };

    //Save Band Friendly Name
    const saveFriendlyName = async () => {
      await Sentry.startSpan(
        { name: "Save Band Friendly Name", op: "http.request" },
        async (span) => {
          try {
            const response = await fetchWithRefresh(import.meta.env.VITE_API_URL + "Bands/SaveFriendlyName", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                bandId: band.BandID,
                friendlyName: step2Answers.bandNickname,
              }),
            }, logout, updateTokens, (message) => { toast.info(message); });
    
            span.setAttribute("http.status_code", response.status);
            if (!response.ok) {
              span.setStatus("error");
              allSuccessful = false;
            }
          } catch (error) {
            span.setStatus("error");
            Sentry.captureException(error);
            LogErrors(user, logout, updateTokens, "Error saving band's friendly name: " + error);
            allSuccessful = false;
          } finally {
            span.end();
          }
        }
      );
    };
  
    // Save Additional ICE Contacts
    const saveAllAdditionalICEContacts = async () => {
      await Sentry.startSpan(
        { name: "Save Additional ICE Contacts", op: "http.request" },
        async (span) => {
          const promises = step2Answers.additionalICEContacts.map((contact) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + "Profile/SaveAdditionalICEContact", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                additionalICEContactId: contact?.additionalICEContactId,
                participantId: band.ParticipantId,
                additionalICEName: contact.name,
                additionalICENumber: contact.phoneNumber,
                additionalICERelationship: contact.relation,
                emergencySMS: contact.sms === "1" || contact.sms === "on" ? 1 : 0,
              })
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { contactName: contact.name, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { contactName: contact.name, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save ICE contact: ${result.contactName}`);
              allSuccessful = false;
            }
          });
    
          span.end();
        }
      );
    };
  
    // Save Medical History
    const saveAllMedicalHistory = async () => {
      await Sentry.startSpan(
        { name: "Save Medical History", op: "http.request" },
        async (span) => {
          const promises = step3Answers.map((history) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + "Profile/SaveMedicalHistoryItem", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                medicalHistoryId: history?.MedicalHistoryId,
                participantId: band.ParticipantId,
                conditionName: history.name || history.ConditionName,
                notes: history.additionalInformation || history.Notes,
                ongoing: history.ongoing || history.OnGoing ? 1 : 0,
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { historyName: history.name, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { historyName: history.name, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save medical history: ${result.historyName}`);
              allSuccessful = false;
            }
          });
    
          span.end();
        }
      );
    };
  
    // Save Allergies
    const saveAllAllergies = async () => {
      await Sentry.startSpan(
        { name: "Save Allergies", op: "http.request" },
        async (span) => {
          const promises = step4Answers.map((allergy) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + "Profile/SaveAllergy", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                allergyId: allergy?.AllergyId,
                participantId: band.ParticipantId,
                conditionName: allergy.name || allergy?.ConditionName,
                notes: allergy.additionalInformation || allergy?.Notes,
                ongoing: allergy.ongoing || allergy?.OnGoing ? 1 : 0,
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { allergyName: allergy.name, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { allergyName: allergy.name, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save allergy: ${result.allergyName}`);
              allSuccessful = false;
            }
          });
    
          span.end();
        }
      );
    };
  
    // Save Medications
    const saveAllMedications = async () => {
      await Sentry.startSpan(
        { name: "Save Medications", op: "http.request" },
        async (span) => {
          const promises = step5Answers.map((medication) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + "Profile/SaveMedication", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                medicationId: medication?.MedicationId,
                participantId: band.ParticipantId,
                medicationName: medication.medicationName || medication.MedicationName,
                dosage: medication.medicationDosage || medication.Dosage,
                frequency: medication.medicationFrequency || medication.Frequency,
                ongoing: medication.ongoing || medication.OnGoing ? 1 : 0,
                notes: medication.additionalInformation || medication.Notes || "",
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { medicationName: medication.medicationName, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { medicationName: medication.medicationName, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save medication: ${result.medicationName}`);
              allSuccessful = false;
            }
          });
    
          span.end();
        }
      );
    };

    const deleteAllAdditionalICEContacts = async () => {
      await Sentry.startSpan(
        { name: "Delete Additional ICE Contacts", op: "http.request" },
        async (span) => {
          const promises = step2AnswersDelete.map((contact) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + `Profile/DeleteAdditionalICEContact?additionalicecontactid=${contact?.AdditionalICEContactId}`, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { AdditionalICEContactId: contact.AdditionalICEContactId, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { AdditionalICEContactId: contact.AdditionalICEContactId, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete contact: ${result?.AdditionalICEContactId}`);
            }
          });
    
          span.end();
        }
      );
    };

    const deleteAllMedicalHistory = async () => {
      await Sentry.startSpan(
        { name: "Delete Medical History", op: "http.request" },
        async (span) => {
          const promises = step3AnswersDelete.map((history) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + `Profile/DeleteMedicalHistoryItem?medicalhistoryid=${history?.MedicalHistoryId}`, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { MedicalHistoryId: history.MedicalHistoryId, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { MedicalHistoryId: history.MedicalHistoryId, ok: false, error };
            })
          );

          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete medical history: ${result.MedicalHistoryId}`);
            }
          });

          span.end();
        }
      );
    };

    const deleteAllAllergies = async () => {
      await Sentry.startSpan(
        { name: "Delete Allergies", op: "http.request" },
        async (span) => {
          const promises = step4AnswersDelete.map((allergy) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + `Profile/DeleteAllergy?allergyid=${allergy?.AllergyId}`, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { AllergyId: allergy.AllergyId, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { AllergyId: allergy.AllergyId, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete allergy: ${result.AllergyId}`);
            }
          });
    
          span.end();
        }
      );
    };    

    const deleteAllMedications = async () => {
      await Sentry.startSpan(
        { name: "Delete Medications", op: "http.request" },
        async (span) => {
          const promises = step5AnswersDelete.map((medication) =>
            fetchWithRefresh(import.meta.env.VITE_API_URL + `Profile/DeleteMedication?medicationid=${medication?.MedicationId}`, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute("http.status_code", response.status);
              return { medicationid: medication.MedicationId, ok: response.ok };
            })
            .catch((error) => {
              span.setStatus("error");
              Sentry.captureException(error);
              return { medicationid: medication.MedicationId, ok: false, error };
            })
          );
    
          const results = await Promise.all(promises);
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete medication: ${result.MedicationId}`);
            }
          });
    
          span.end();
        }
      );
    };
  
    // Execute all API calls
    await startBandActivation();

    async function deleteThenSaveContacts() {
      if (step2AnswersDelete.length > 0) {
        await deleteAllAdditionalICEContacts();
      }
    
      if (step2Answers.additionalICEContacts.length > 0) {
        await saveAllAdditionalICEContacts(); 
      }
    }

    await saveUserProfile();
    await saveFriendlyName();
    await deleteThenSaveContacts();

    if(step3AnswersDelete.length > 0){
      await deleteAllMedicalHistory();
    }

    if(step3Answers.length > 0){
      await saveAllMedicalHistory();
    }

    if(step4AnswersDelete.length > 0){
      await deleteAllAllergies();
    }

    if(step4Answers.length > 0){
      await saveAllAllergies();
    }

    if(step5AnswersDelete.length > 0){
      await deleteAllMedications();
    }

    if(step5Answers.length > 0){
      await saveAllMedications();
    }

    if (allSuccessful) {
      await Sentry.startSpan(
        { name: "Activate Band", op: "http.request" },
        async (span) => {
          try {
            const activateBandResponse = await fetchWithRefresh(
              import.meta.env.VITE_API_URL + `Bands/ActivateBand/?bandId=${band.BandID}&userTriggering=${user.id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user.token}`,
                },
              },
              logout,
              updateTokens,
              (message) => {
                toast.info(message);
              }
            );
    
            span.setAttribute("http.status_code", activateBandResponse.status);
    
            if (!activateBandResponse.ok) {
              span.setStatus("error");
              setActivatingBand(false);
              setOpen(true);
              setDrawerContent(
                <div className="w-full">
                  <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">ERROR</h2>
                    <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
                  </div>
                  <div className="mt-6 flex flex-col justify-stretch gap-6">
                    <h3 className="text-[24px]">There&apos;s been a problem activating your band.</h3>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                  </div>
                </div>
              );
              LogErrors(user, logout, updateTokens, "Failed to activate band.");
            } else {
              setActivatingBand(false);
              navigate(`/`);
              setOpen(true);
              setDrawerContent(
                <div className="w-full">
                  <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">BAND ACTIVATED</h2>
                    <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
                  </div>
                  <div className="mt-6 flex flex-col justify-stretch gap-6">
                    <h3 className="text-[24px]">You have successfully activated your band.</h3>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                  </div>
                </div>
              );
              localStorage.removeItem("activeBandStep2");
              localStorage.removeItem("activeBandStep2Delete");
              localStorage.removeItem("activeBandStep3");
              localStorage.removeItem("activeBandStep3Delete");
              localStorage.removeItem("activeBandStep4");
              localStorage.removeItem("activeBandStep4Delete");
              localStorage.removeItem("activeBandStep5");
              localStorage.removeItem("activeBandStep5Delete");
            }
          } catch (error) {
            span.setStatus("error");
            Sentry.captureException(error);
            setActivatingBand(false);
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">ERROR</h2>
                  <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col justify-stretch gap-6">
                  <h3 className="text-[24px]">There&apos;s been a problem activating your band.</h3>
                  <Button onClick={() => setOpen(false)}>Close</Button>
                </div>
              </div>
            );
            LogErrors(user, logout, updateTokens, "Error activating band: " + error);
          } finally {
            span.end();
          }
        }
      );
    } else {
      await Sentry.startSpan(
        { name: "Rollback Band Activation", op: "http.request" },
        async (span) => {
          try {
            const undoActivationResponse = await fetchWithRefresh(
              import.meta.env.VITE_API_URL + `Bands/RollbackBandActivation/?participantId=${band.ParticipantId}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user.token}`,
                },
              },
              logout,
              updateTokens,
              (message) => {
                toast.info(message);
              }
            );
    
            span.setAttribute("http.status_code", undoActivationResponse.status);
    
            if (!undoActivationResponse.ok) {
              LogErrors(user, logout, updateTokens, "Failed to undo activate band process.");
            } else {
              setOpen(true);
              setDrawerContent(
                <div className="w-full">
                  <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">ERROR</h2>
                    <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
                  </div>
                  <div className="mt-6 flex flex-col justify-stretch gap-6">
                    <h3 className="text-[24px]">There&apos;s been a problem activating your band.</h3>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                  </div>
                </div>
              );
            }
          } catch (error) {
            Sentry.captureException(error);
            LogErrors(user, logout, updateTokens, "Failed to undo activate band process: " + error);
          } finally {
            span.end();
          }
        }
      );
    
      setActivatingBand(false);
      LogErrors(user, logout, updateTokens, "Not all previous API calls were successful. Band activation aborted.");
    }
  }    

  return (
    <div className="w-full flex flex-col justify-stretch mb-[80px]">
      <div className="p-[24px] flex flex-col gap-14">
        <ProgressBar step={5} complete />

        <BandCard
          size="sm"
          band={band}
          participant={profile}
          tempImage={step2Answers?.profilePicture}
        />

        <div className="flex flex-col justify-stretch gap-4">
          <h3>{step2Answers?.participantName}</h3>
          <span className="text-sportsGrey font-poppins ">
            Last Updated • {new Date().toLocaleDateString()}
          </span>

          <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-6 !mt-6" />
          <p className="font-semibold">Date of Birth</p>
          <p className="text-sportsGrey">{formatDate(step2Answers?.participantDOB)}</p>

          <p className="font-semibold">Primary Emergency Contact Name (ICE)</p>
          <p className="text-sportsGrey">{step2Answers?.bandICEName}</p>

          <p className="font-semibold">Primary Emergency Contact Number</p>
          <p className="text-sportsGrey">{step2Answers?.bandICEPhoneNumber}</p>

          <Toggle 
            name="AutomaticSMSComplete"
            label="Automatic SMS"
            defaultChecked={step2Answers?.EmergencySMSPrimaryContact === "1" || step2Answers?.EmergencySMSPrimaryContact === "on" ? true : false}
            disabled
            labelClassName="!font-semibold"
          />

          <p className="font-semibold">Primary Emergency Contact Relationship</p>
          <p className="text-sportsGrey">{step2Answers?.bandICERelation}</p>

          <p className="font-semibold">Primary Medical Condition</p>
          <p className="text-sportsGrey">{step2Answers?.PrimaryMedicalCondition}</p>

          <p className="font-semibold">Band Nickname</p>
          <p className="text-sportsGrey">{step2Answers?.bandNickname}</p>

          {step2Answers?.uploadedVoiceNote && (
            <>
              <p className="font-semibold">Voice Note</p>
              <VoiceNote existingAudio={step2Answers?.uploadedVoiceNote} label={false} />
            </>
          )}

          {step2Answers?.participantNotes && (
            <>
              <p className="font-semibold">Notes</p>
              <p className="text-sportsGrey">{step2Answers?.participantNotes}</p>
            </>
          )}
        </div>
      </div>
      <div className="bg-sportsLightGrey border-[#E3E3E3] border-t-[1px] p-[24px] flex justify-center flex-col gap-6">
        
        {step3Answers?.length > 0 && (
          <DropDown title="Medical History" icon="medical">
            <div className='flex flex-col gap-y-[15px]'>
              {step3Answers?.map((condition, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step3Answers.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Condition Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition.name || condition.ConditionName}</p>
                      <p className='font-poppins text-[12px] leading-[12px] text-sportsGrey'>Date Added • {formatDate(condition.dateAdded || condition.DateAdded)}</p>
                    </div>
                    <Link to="/add-band/3" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition.ongoing || condition.OnGoing ? "Yes" : "No"}</p>
                    </div>
                    <Link to="/add-band/3" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  {condition.additionalInformation && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition.additionalInformation || condition.Notes}</p>
                      </div>
                      <Link to="/add-band/4" state={{band: band, profile: profile}}>
                        <EditPencilIcon />
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropDown>
        )}

        {step4Answers?.length > 0 && (
          <DropDown title="Allergies" icon="allergy">
            <div className='flex flex-col gap-y-[15px]'>
              {step4Answers?.map((allergy, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step4Answers.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Allergy Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy.name || allergy.ConditionName}</p>
                      <p className='font-poppins text-[12px] leading-[12px] text-sportsGrey'>Date Added • {formatDate(allergy.dateAdded || allergy.DateAdded)}</p>
                    </div>
                    <Link to="/add-band/4" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  {/* <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy.ongoing || allergy.OnGoing ? "Yes" : "No"}</p>
                    </div>
                    <Link to="/add-band/4" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div> */}

                  {allergy.additionalInformation && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy.additionalInformation || allergy.Notes}</p>
                      </div>
                      <Link to="/add-band/4" state={{band: band, profile: profile}}>
                        <EditPencilIcon />
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropDown>
        )}

        {step5Answers?.length > 0 && (
          <DropDown title="Medications" icon="medication">
            <div className='flex flex-col gap-y-[15px]'>
              {step5Answers?.map((medication, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step5Answers.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Medication Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.medicationName || medication.MedicationName }</p>
                      <p className='font-poppins text-[12px] leading-[12px] text-sportsGrey'>Date Added • {formatDate(medication.dateAdded || medication.DateAdded)}</p>
                    </div>
                    <Link to="/add-band/5" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Dosage</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.medicationDosage || medication.Dosage }</p>
                    </div>
                    <Link to="/add-band/5" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Frequency</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.medicationFrequency || medication.Frequency}</p>
                    </div>
                    <Link to="/add-band/5" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.ongoing || medication.OnGoing ? "Yes" : "No"}</p>
                    </div>
                    <Link to="/add-band/5" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  {medication.additionalInformation && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.additionalInformation || medication.Notes}</p>
                      </div>
                      <Link to="/add-band/5" state={{band: band, profile: profile}}>
                        <EditPencilIcon />
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropDown>
        )}

        {step2Answers?.additionalICEContacts.length > 0 && (
          <DropDown title="Other Emergency Contacts" icon="contact">
            <div className='flex flex-col gap-y-[15px]'>
              {step2Answers?.additionalICEContacts?.map((contact, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step2Answers?.additionalICEContacts.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Emergency Contact {index + 1}</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.name} - {contact?.phoneNumber}</p>
                    </div>
                    <Link to="/add-band/2" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Relationship</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.relation}</p>
                    </div>
                    <Link to="/add-band/2" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Automated SMS</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.sms === "1" || contact?.sms === "on" ? "Enabled" : "Disabled"}</p>
                    </div>
                    <Link to="/add-band/2" state={{band: band, profile: profile}}>
                      <EditPencilIcon />
                    </Link>
                  </div>
                </div>
              ))}

              <Link to="/add-band/2" state={{band: band, profile: profile}} className="mx-auto">
                <Button variant="tertiary"><AddIcon className="w-4 h-4"/> Add</Button>
              </Link>
            </div>
          </DropDown>
        )}

        <form onSubmit={handleBandActivation} className="flex flex-col gap-[10px]">
          <div className="font-semibold flex gap-5 pr-[20px]">
            <CheckBox 
              name="confirmation" 
              onInput={(e) => e.target.setCustomValidity("")}
              onInvalid={(e) => e.target.setCustomValidity('Our partners rely on this information to assist you in the event of an emergency. Please confirm your details are complete and up to date.')}
              required
            /> 
            I confirm all the information provided is correct and up to date
          </div>
          {error && <p className="text-sportsRed text-[16px] leading-[24px] ml-[46px]">{error}</p>}

          <Button submit={true} disabled={activatingBand} className="mt-6">
            {activatingBand ? "Activating " : "Activate "}Band
          </Button>
        </form>
      </div>
    </div>
  );
}